import { Injectable, inject } from '@angular/core';
import { Observable, forkJoin, map, of, switchMap } from 'rxjs';
import { TipoInstituto } from '../../enum/parametros/tipo-instituto.enum';
import {
  NOME_TIPO_INSTITUTO,
  tipoInstitutoDoNome,
  tipoInstitutoDtoToInstituto,
} from '../../mappers/parametros/instituto-param-api.mapper';
import {
  configuracaoToCreateInstitutoParamCommand,
  configuracaoToPatchInstitutoParamCommand,
  configuracaoToResgateCommand,
  documentoTipoDtoToOpcao,
  documentosDtoToDocumentos,
  provedorAssinaturaDtoToProvedor,
  resgateDtoToConfiguracaoInstituto,
} from '../../mappers/parametros/resgate-configuracao-api.mapper';
import { InstitutoParamResponseDto } from '../../models/parametros/instituto-param-api.model';
import { InstitutoParamDocResponseDto } from '../../models/parametros/instituto-param-doc-api.model';
import { PlanoResumo } from '../../models/parametros/plano.model';
import { InstitutoParamResgateResponseDto } from '../../models/parametros/resgate-configuracao-api.model';
import {
  ConfiguracaoInstituto,
  Instituto,
  ProvedorAssinatura,
  ResgateConfiguracao,
  ResgateDocumento,
  ResgateDocumentoOpcao,
} from '../../models/parametros/resgate-configuracao.model';
import { InstitutosParamApiService } from './institutos-param-api.service';
import { InstitutosParamDocsApiService } from './institutos-param-docs-api.service';
import { PlanosService } from './planos.service';
import { ResgateConfiguracaoApiService } from './resgate-configuracao-api.service';

/** Maior página aceita por GET /v1/institutos-param-docs. */
const DOCUMENTOS_POR_PAGINA = 100;

/** Erro ao associar um instituto cujo tipo não está cadastrado em `/v1/tipos-instituto`. */
export class TipoInstitutoNaoCadastradoError extends Error {
  constructor(readonly nome: string) {
    super(`O tipo de instituto ${nome} não está cadastrado.`);
    this.name = 'TipoInstitutoNaoCadastradoError';
  }
}

interface RegistrosResgate {
  institutoParam: InstitutoParamResponseDto | null;
  resgate: InstitutoParamResgateResponseDto | null;
}

/**
 * Configuração do resgate de cada plano: o Resgate associado ao plano (`/v1/institutos-param`,
 * com status e vigência), os parâmetros dele (`/v1/institutos-param-resgate`) e os documentos
 * obrigatórios (`/v1/institutos-param-docs`, nomeados pelo catálogo `/v1/tipos-documento`).
 */
@Injectable({
  providedIn: 'root',
})
export class ResgateConfiguracaoService {
  private readonly planosService = inject(PlanosService);
  private readonly institutosParamApiService = inject(InstitutosParamApiService);
  private readonly resgateApiService = inject(ResgateConfiguracaoApiService);
  private readonly docsApiService = inject(InstitutosParamDocsApiService);

  /** Plano exibido no cabeçalho da parametrização. */
  obterPlano(planoId: number): Observable<PlanoResumo> {
    return this.planosService
      .obterPlano(planoId)
      .pipe(map(({ id, codigo, nome, tipo, status, ultimaAtualizacao }) => ({ id, codigo, nome, tipo, status, ultimaAtualizacao })));
  }

  /** Institutos da busca (tipos ativos); os não habilitados aparecem, mas não podem ser escolhidos. */
  listarInstitutos(): Observable<Instituto[]> {
    return this.institutosParamApiService
      .listarTiposInstituto({ ativo: 'S' })
      .pipe(
        map((tipos) =>
          tipos.map(tipoInstitutoDtoToInstituto).filter((instituto): instituto is Instituto => instituto !== null),
        ),
      );
  }

  /** Tipos de documento ativos, que podem ser incluídos na configuração. */
  listarDocumentosDisponiveis(): Observable<ResgateDocumentoOpcao[]> {
    return this.docsApiService
      .listarTiposDocumento({ ativo: 'S' })
      .pipe(map((tipos) => tipos.map(documentoTipoDtoToOpcao)));
  }

  /** Provedores de assinatura ativos, para o campo "Provedor". */
  listarProvedores(): Observable<ProvedorAssinatura[]> {
    return this.resgateApiService
      .listarProvedoresAssinatura({ ativo: 'S' })
      .pipe(map((provedores) => provedores.map(provedorAssinaturaDtoToProvedor)));
  }

  /** Retorna `null` quando o resgate ainda não tem configuração salva no plano. */
  obterConfiguracao(planoId: number): Observable<ConfiguracaoInstituto | null> {
    return this.obterRegistros(planoId).pipe(
      switchMap(({ institutoParam, resgate }) =>
        institutoParam && resgate
          ? this.obterDocumentos(institutoParam.id).pipe(
              map((documentos) => resgateDtoToConfiguracaoInstituto(institutoParam, resgate, documentos)),
            )
          : of(null),
      ),
    );
  }

  /**
   * Associa o Resgate ao plano, se ainda não estiver associado, cria ou atualiza a configuração
   * dele e sincroniza os documentos obrigatórios. Devolve o registro salvo.
   */
  salvarConfiguracao(planoId: number, configuracao: ResgateConfiguracao): Observable<ConfiguracaoInstituto> {
    return this.obterRegistros(planoId).pipe(
      switchMap(({ institutoParam, resgate }) =>
        this.gravarInstitutoParam(planoId, institutoParam, configuracao).pipe(
          switchMap((gravado) =>
            this.gravarResgate(gravado.id, resgate, configuracao).pipe(
              switchMap((resgateGravado) =>
                this.gravarDocumentos(gravado.id, configuracao.documentos).pipe(
                  map(() => resgateDtoToConfiguracaoInstituto(gravado, resgateGravado, configuracao.documentos)),
                ),
              ),
            ),
          ),
        ),
      ),
    );
  }

  /**
   * Copia a configuração do resgate de um instituto parametrizado para outro (plano duplicado),
   * com os documentos obrigatórios ativos.
   */
  copiarResgate(institutoParamOrigem: number, institutoParamDestino: number): Observable<void> {
    return this.resgateApiService.listarResgates({ id_inst_param: institutoParamOrigem }).pipe(
      switchMap(([resgate]) =>
        resgate
          ? this.resgateApiService
              .clonarResgate(resgate.id, { id_inst_param: institutoParamDestino })
              .pipe(switchMap(() => this.copiarDocumentos(institutoParamOrigem, institutoParamDestino)))
          : of(null),
      ),
      map(() => undefined),
    );
  }

  /** O Resgate do plano é identificado pelo `nome` do tipo de instituto agregado. */
  private obterRegistros(planoId: number): Observable<RegistrosResgate> {
    return this.institutosParamApiService.listarInstitutosParam({ id_plano: planoId }).pipe(
      map((institutos) =>
        institutos.find((instituto) => tipoInstitutoDoNome(instituto.tipo_instituto?.nome) === TipoInstituto.Resgate),
      ),
      switchMap((institutoParam) =>
        institutoParam
          ? this.resgateApiService
              .listarResgates({ id_inst_param: institutoParam.id })
              .pipe(map(([resgate]) => ({ institutoParam, resgate: resgate ?? null })))
          : of({ institutoParam: null, resgate: null }),
      ),
    );
  }

  /** `id_tp_inst` do Resgate em PRE_TBL_INSTITUTO_TIPOS; falha se o tipo não estiver cadastrado. */
  private obterIdTipoResgate(): Observable<number> {
    return this.institutosParamApiService
      .listarTiposInstituto({ nome: NOME_TIPO_INSTITUTO[TipoInstituto.Resgate] })
      .pipe(
        map((tipos) => {
          const resgate = tipos.find((tipo) => tipoInstitutoDoNome(tipo.nome) === TipoInstituto.Resgate);
          if (!resgate) {
            throw new TipoInstitutoNaoCadastradoError(NOME_TIPO_INSTITUTO[TipoInstituto.Resgate]);
          }
          return resgate.id;
        }),
      );
  }

  private gravarInstitutoParam(
    planoId: number,
    institutoParam: InstitutoParamResponseDto | null,
    configuracao: ResgateConfiguracao,
  ): Observable<InstitutoParamResponseDto> {
    return institutoParam
      ? this.institutosParamApiService.atualizarInstitutoParam(
          institutoParam.id,
          configuracaoToPatchInstitutoParamCommand(configuracao),
        )
      : this.obterIdTipoResgate().pipe(
          switchMap((idTipoInstituto) =>
            this.institutosParamApiService.criarInstitutoParam(
              configuracaoToCreateInstitutoParamCommand(planoId, idTipoInstituto, configuracao),
            ),
          ),
        );
  }

  private gravarResgate(
    institutoParamId: number,
    resgate: InstitutoParamResgateResponseDto | null,
    configuracao: ResgateConfiguracao,
  ): Observable<InstitutoParamResgateResponseDto> {
    const command = configuracaoToResgateCommand(configuracao);
    return resgate
      ? this.resgateApiService.atualizarResgate(resgate.id, command)
      : this.resgateApiService.criarResgate({ ...command, id_inst_param: institutoParamId });
  }

  /** Documentos ativos do instituto parametrizado, com o nome do catálogo de tipos. */
  private obterDocumentos(institutoParamId: number): Observable<ResgateDocumento[]> {
    return forkJoin([
      this.listarDocumentosGravados(institutoParamId),
      this.docsApiService.listarTiposDocumento(),
    ]).pipe(map(([documentos, tipos]) => documentosDtoToDocumentos(documentos, tipos)));
  }

  /**
   * Deixa ativos só os documentos da configuração. Sem DELETE físico e com `id_inst_param` +
   * `id_doc_tipo` únicos: o documento novo é incluído, o que já existia é reativado ou inativado.
   */
  private gravarDocumentos(institutoParamId: number, documentos: readonly ResgateDocumento[]): Observable<void> {
    const exigidos = new Set(documentos.map((documento) => documento.id));
    return this.listarDocumentosGravados(institutoParamId).pipe(
      switchMap((gravados) => {
        const gravadosPorTipo = new Set(gravados.map((gravado) => gravado.id_doc_tipo));
        const gravacoes = [
          ...[...exigidos]
            .filter((idDocTipo) => !gravadosPorTipo.has(idDocTipo))
            .map((idDocTipo) =>
              this.docsApiService.criarDocumento({
                id_inst_param: institutoParamId,
                id_doc_tipo: idDocTipo,
                fl_ativo: true,
              }),
            ),
          ...gravados
            .filter((gravado) => gravado.fl_ativo !== exigidos.has(gravado.id_doc_tipo))
            .map((gravado) => this.docsApiService.alterarStatusDocumento(gravado.id, { fl_ativo: !gravado.fl_ativo })),
        ];
        return gravacoes.length ? forkJoin(gravacoes) : of([]);
      }),
      map(() => undefined),
    );
  }

  private copiarDocumentos(institutoParamOrigem: number, institutoParamDestino: number): Observable<void> {
    return this.listarDocumentosGravados(institutoParamOrigem).pipe(
      switchMap((documentos) => {
        const ativos = documentos.filter((documento) => documento.fl_ativo);
        return ativos.length
          ? forkJoin(
              ativos.map((documento) =>
                this.docsApiService.criarDocumento({
                  id_inst_param: institutoParamDestino,
                  id_doc_tipo: documento.id_doc_tipo,
                  fl_ativo: true,
                }),
              ),
            )
          : of([]);
      }),
      map(() => undefined),
    );
  }

  /** Todos os documentos (ativos e inativos) do instituto parametrizado, percorrendo as páginas. */
  private listarDocumentosGravados(institutoParamId: number): Observable<InstitutoParamDocResponseDto[]> {
    const pagina = (numero: number) =>
      this.docsApiService.listarDocumentos({
        id_inst_param: institutoParamId,
        pagina: numero,
        tamanho_pagina: DOCUMENTOS_POR_PAGINA,
      });

    return pagina(1).pipe(
      switchMap((primeira) => {
        const restantes = Array.from({ length: Math.max((primeira.total_paginas ?? 1) - 1, 0) }, (_, i) =>
          pagina(i + 2),
        );
        return restantes.length ? forkJoin(restantes).pipe(map((outras) => [primeira, ...outras])) : of([primeira]);
      }),
      map((paginas) => paginas.flatMap((resposta) => resposta.itens ?? [])),
    );
  }
}
